import { Controller } from "@hotwired/stimulus"

// AI project setup (#53): sub-list upload → poll → preview → create contracts.
export default class extends Controller {
  static targets = ["subsStatus", "subsPreview", "subsBody"]
  static values = { presignUrl: String, runUrl: String, statusUrl: String, applyUrl: String }

  connect() {
    const msg = sessionStorage.getItem("builder-finance.flash")
    if (msg) {
      sessionStorage.removeItem("builder-finance.flash")
      this._showToast(msg)
    }
  }

  disconnect() { clearInterval(this._poll) }

  async subListChosen(e) {
    const file = e.currentTarget.files[0]
    if (!file) return
    e.currentTarget.value = ""
    this._status(`Uploading ${file.name}…`)
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const pd = await presign.json()
      if (!presign.ok) throw new Error(pd.error || "Upload refused")
      const u = pd.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("Upload failed")

      this._status("Reading the sub list…")
      const start = await fetch(this.runUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ kind: "subs", s3_key: `uploads/tmp/${u.cache_name}`, filename: file.name, mime_type: u.content_type })
      })
      const sd = await start.json()
      if (!start.ok) throw new Error(sd.error || "Could not start")
      this._pollRun(sd.run_id)
    } catch (err) {
      this._status("")
      this._showToast(err.message, "alert")
    }
  }

  _pollRun(runId) {
    let ticks = 0
    this._poll = setInterval(async () => {
      if (++ticks > 60) { clearInterval(this._poll); this._status("Taking too long — try again."); return }
      const resp = await fetch(`${this.statusUrlValue}?run_id=${runId}`, { headers: { "Accept": "application/json" } })
      const data = await resp.json()
      if (data.status === "done") {
        clearInterval(this._poll)
        this._status("")
        this._renderSubs((data.result && data.result.subs) || [])
      } else if (data.status === "failed") {
        clearInterval(this._poll)
        this._status("")
        this._showToast(data.error || "The list couldn't be read.", "alert")
      }
    }, 2500)
  }

  _renderSubs(subs) {
    if (!subs.length) return this._showToast("Nothing recognizable in that file — add subs manually on Sub Payments.", "alert")
    const esc = (s) => (s || "").toString().replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    this.subsBodyTarget.innerHTML = subs.map((s) => `
      <tr>
        <td class="px-2 py-1.5 font-medium">${esc(s.name)}</td>
        <td class="px-2 py-1.5 text-gray-500">${esc(s.trade)}</td>
        <td class="px-2 py-1.5 text-gray-500">${esc([s.contact_name, s.contact_email].filter(Boolean).join(" · "))}</td>
        <td class="px-2 py-1.5 text-right tabular-nums">${s.amount ? "$" + Number(s.amount).toLocaleString("en-US", { minimumFractionDigits: 2 }) : "—"}</td>
      </tr>`).join("")
    this.subsPreviewTarget.classList.remove("hidden")
    this._showToast(`Found ${subs.length} subs — review and create`)
  }

  async applySubs() {
    try {
      const resp = await fetch(this.applyUrlValue, { method: "POST", headers: this._headers() })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not create the contracts")
      sessionStorage.setItem("builder-finance.flash", `${data.created} sub contracts created`)
      window.location.href = data.url
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  _status(text) {
    this.subsStatusTarget.textContent = text
    this.subsStatusTarget.classList.toggle("hidden", !text)
  }

  _headers() {
    return {
      "Content-Type": "application/json", "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _showToast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    div.textContent = message
    document.body.prepend(div)
  }
}
